/**
 * Fragen entstehen aus dem geprüften Bestand, nicht aus einer zweiten Textdatei.
 *
 * Damit kann keine Frage von den Daten abweichen, und jede Antwort trägt ihre Fundstelle.
 * Die falschen Antworten werden so gewählt, dass sie nie dasselbe Bild zeigen wie die
 * richtige: eine Frage mit zwei richtigen Antworten wäre ein Fehler, kein Schwierigkeitsgrad.
 */
import { binnenSignale, binnenState, binnenStates, buoys, lightStates, signals } from './data'
import type { BinnenLightStateData, BinnenSignalData, BuoyData, LightStateData, SignalData } from './schema'

export type QuizKind = 'light-state' | 'binnen-state' | 'buoy' | 'signal' | 'binnen-signal'

export interface QuizOption {
  id: string
  label: { en: string; de: string }
}

export interface QuizQuestion {
  kind: QuizKind
  /** Was gezeigt wird: Zustands- oder Seezeichen-ID. */
  subject: string
  prompt: { en: string; de: string }
  options: QuizOption[]
  correct: number
  /** Karten-ID für die Wiederholung. */
  cardId: string
}

/** Kleiner, schneller Zufall mit Saat: dieselbe Saat ergibt dieselbe Frage. */
export function rng(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296
  }
}

function shuffle<T>(items: T[], random: () => number): T[] {
  const out = [...items]
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1))
    ;[out[i], out[j]] = [out[j]!, out[i]!]
  }
  return out
}

/**
 * Das Bild eines Lichterzustands als Zeichenkette. Zwei Zustände mit derselben
 * Zeichenkette sind bei Nacht nicht zu unterscheiden (etwa der starre Schubverband und
 * das Maschinenfahrzeug) und dürfen nie in derselben Frage stehen.
 */
export function lightSignature(state: LightStateData): string {
  return state.lights
    .map((l) => `${l.type}:${l.colour}:${l.mount}:${l.tier ?? 0}:${l.rhythm ?? 'steady'}`)
    .sort()
    .join('|')
}

/**
 * Dasselbe für Schallsignale: die Folge der Töne. Regel 34(e) und Regel 35(a) klingen
 * beide wie ein langer Ton, und in einer Frage dürfen sie deshalb nicht nebeneinander
 * stehen. Auseinanderhalten kann man sie nur an der Lage, und die nennt die Frage.
 */
export function signalSignature(signal: SignalData): string {
  return signal.pattern.map((p) => `${p.kind}x${p.count}`).join('|')
}

/** Dasselbe für Seezeichen: Farben, Form, Toppzeichen und Kennung. */
export function buoySignature(buoy: BuoyData): string {
  return [buoy.colours.join('-'), buoy.pattern, buoy.shape, buoy.topmark, buoy.character ?? 'unbefeuert'].join('|')
}

const PROMPTS = {
  'light-state': {
    en: 'Which vessel shows these lights?',
    de: 'Welches Fahrzeug zeigt dieses Lichterbild?',
  },
  buoy: {
    en: 'Which mark is this?',
    de: 'Welches Seezeichen ist das?',
  },
}

/**
 * Die Frage nach einem Schallsignal muss die Lage nennen, sonst ist sie nicht zu
 * beantworten: ein langer Ton heißt im engen Fahrwasser etwas anderes als im Nebel.
 */
const SIGNAL_PROMPTS: Record<SignalData['context'], { en: string; de: string }> = {
  manoeuvre: {
    en: 'You see the other vessel and hear this. What is she doing?',
    de: 'Du siehst das andere Fahrzeug und hörst das. Was tut es?',
  },
  warning: {
    en: 'You hear this from a vessel in sight. What does it mean?',
    de: 'Du hörst das von einem Fahrzeug in Sicht. Was bedeutet es?',
  },
  'narrow-channel': {
    en: 'In a narrow channel you hear this. What does it mean?',
    de: 'Im engen Fahrwasser hörst du das. Was bedeutet es?',
  },
  'restricted-visibility': {
    en: 'In fog you hear this. Who is sounding it?',
    de: 'Im Nebel hörst du das. Wer gibt das Signal?',
  },
  identity: {
    en: 'In fog you hear this in addition to the other signals. Who is it?',
    de: 'Im Nebel hörst du das zusätzlich zu den anderen Signalen. Wer ist es?',
  },
  distress: {
    en: 'You hear this. What does it mean?',
    de: 'Du hörst das. Was bedeutet es?',
  },
}

function pickDistractors<T>(
  pool: T[],
  correct: T,
  signature: (item: T) => string,
  related: (item: T) => boolean,
  random: () => number,
  count = 3,
): T[] {
  // Jedes Bild darf in einer Frage nur einmal vorkommen, auch unter den falschen
  // Antworten: das Ruderboot und der kleine Segler zeigen beide nur eine Handlampe.
  const used = new Set([signature(correct)])
  const usable = pool.filter((x) => x !== correct)
  const near = shuffle(usable.filter(related), random)
  const far = shuffle(
    usable.filter((x) => !related(x)),
    random,
  )
  const picked: T[] = []
  for (const candidate of [...near, ...far]) {
    const key = signature(candidate)
    if (used.has(key)) continue
    used.add(key)
    picked.push(candidate)
    if (picked.length === count) break
  }
  return picked
}

/** Dieselbe Signatur für die Binnenbilder: zwei Bilder mit gleichen Lichtern trennt nichts. */
export function binnenSignature(state: BinnenLightStateData): string {
  return state.lights
    .map((l) => `${l.type}:${l.colour}:${l.mount}:${l.tier ?? 0}:${l.rhythm ?? 'steady'}`)
    .sort()
    .join('|')
}

/**
 * Die Frage nach einem Binnenbild.
 *
 * Die falschen Antworten kommen ausschließlich aus dem Binnenbestand. Ein Seezustand
 * unter den Vorschlägen wäre nicht nur unpassend, er wäre oft sogar richtig: ein
 * Maschinenfahrzeug führt auf See und auf dem Rhein dieselben Seitenlichter, und die
 * Frage hätte zwei Antworten.
 */
export function binnenStateQuestion(state: BinnenLightStateData, random: () => number): QuizQuestion {
  const distractors = pickDistractors(
    binnenStates,
    state,
    binnenSignature,
    (other) => other.paragraph === state.paragraph || other.tags.some((t) => state.tags.includes(t)),
    random,
  )
  const options = shuffle(
    [state, ...distractors].map((s) => ({ id: s.id, label: s.title })),
    random,
  )
  return {
    kind: 'binnen-state',
    subject: state.id,
    prompt: PROMPTS['light-state'],
    options,
    correct: options.findIndex((o) => o.id === state.id),
    cardId: `binnen:${state.id}`,
  }
}

export function lightStateQuestion(state: LightStateData, random: () => number): QuizQuestion {
  const distractors = pickDistractors(
    lightStates,
    state,
    lightSignature,
    (other) => other.rule === state.rule || other.tags.some((t) => state.tags.includes(t)),
    random,
  )
  const options = shuffle(
    [state, ...distractors].map((s) => ({ id: s.id, label: s.title })),
    random,
  )
  return {
    kind: 'light-state',
    subject: state.id,
    prompt: PROMPTS['light-state'],
    options,
    correct: options.findIndex((o) => o.id === state.id),
    cardId: `state:${state.id}`,
  }
}

export function buoyQuestion(buoy: BuoyData, random: () => number): QuizQuestion {
  const distractors = pickDistractors(
    buoys,
    buoy,
    buoySignature,
    (other) => other.category === buoy.category || other.region === buoy.region,
    random,
  )
  const options = shuffle(
    [buoy, ...distractors].map((b) => ({ id: b.id, label: b.name })),
    random,
  )
  return {
    kind: 'buoy',
    subject: buoy.id,
    prompt: PROMPTS.buoy,
    options,
    correct: options.findIndex((o) => o.id === buoy.id),
    cardId: `buoy:${buoy.id}`,
  }
}

export function signalQuestion(signal: SignalData, random: () => number): QuizQuestion {
  const distractors = pickDistractors(
    signals,
    signal,
    signalSignature,
    (other) => other.context === signal.context,
    random,
  )
  const options = shuffle(
    [signal, ...distractors].map((s) => ({ id: s.id, label: s.meaning })),
    random,
  )
  return {
    kind: 'signal',
    subject: signal.id,
    prompt: SIGNAL_PROMPTS[signal.context],
    options,
    correct: options.findIndex((o) => o.id === signal.id),
    cardId: `signal:${signal.id}`,
  }
}

/**
 * Was die Frage zu einem Binnen-Schallzeichen nennt, und damit, was es eindeutig macht.
 *
 * Auf dem Rhein heißt derselbe Ton Verschiedenes. Ein kurzer Ton ist als Allgemeines
 * Zeichen ein Kurswechsel nach Steuerbord, bei der Begegnung vom Bergfahrer die Bitte um
 * Vorbeifahrt an Backbord, vom Talfahrer die Zustimmung dazu, und nach einem Widerspruch
 * die Bestätigung. Eine Frage, die nur den Ton nennt, hätte vier richtige Antworten.
 * Diese hier nennt den Abschnitt, wer das Zeichen gibt, worauf es antwortet oder dass es
 * als Erstes kommt, und seinen Namen; zusammen ist das eindeutig, und ein Test hält das
 * für jedes Paar im Bestand fest.
 */
export function binnenSignalMerkmale(z: BinnenSignalData): string {
  return [z.abschnitt, z.wer ?? '-', z.antwortAuf ?? 'zuerst', z.name.de].join('|')
}

const WER_DE: Record<NonNullable<BinnenSignalData['wer']>, string> = {
  bergfahrer: 'der Bergfahrer',
  talfahrer: 'der Talfahrer',
  ueberholender: 'der Überholende',
  vorausfahrender: 'der Vorausfahrende',
}
const WER_EN: Record<NonNullable<BinnenSignalData['wer']>, string> = {
  bergfahrer: 'the upstream vessel',
  talfahrer: 'the downstream vessel',
  ueberholender: 'the overtaking vessel',
  vorausfahrender: 'the vessel ahead',
}

/**
 * Der Name des Zeichens, so wie die Anlage ihn führt, hinter einem Doppelpunkt.
 *
 * Die Anlage nennt jedes Zeichen im Nominativ ("1 kurzer Ton"). In einen Satz gebaut,
 * müsste er gebeugt werden ("gibt einen kurzen Ton", "antwortet mit einem kurzen Ton"),
 * und die erste Fassung tat das nicht: dort stand "antwortet mit 1 kurzer Ton". Hinter
 * einem Doppelpunkt bleibt der Wortlaut der Anlage stehen, wie er ist.
 */
function klingt(z: BinnenSignalData, lang: 'de' | 'en'): string {
  return z.name[lang]
}

/**
 * Die Frage zu einem Binnen-Schallzeichen, in einem Satz, der die Lage nennt.
 *
 * Die Bedeutungen stehen nur auf Deutsch, weil es keine amtliche englische Fassung der
 * Ordnung gibt; im englischen Satz steht der deutsche Wortlaut der Anfrage deshalb in
 * Anführungszeichen, wie ein Zitat.
 */
export function binnenSignalPrompt(z: BinnenSignalData): { de: string; en: string } {
  const vorher = z.antwortAuf ? binnenSignale.find((x) => x.id === z.antwortAuf) : undefined
  const gross = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)
  const werDe = z.wer ? WER_DE[z.wer] : 'ein Fahrzeug'
  const werEn = z.wer ? WER_EN[z.wer] : 'a vessel'

  if (vorher) {
    const vorherDe = gross(vorher.wer ? WER_DE[vorher.wer] : 'das andere Fahrzeug')
    const vorherEn = gross(vorher.wer ? WER_EN[vorher.wer] : 'the other vessel')
    // Beim Überholen ist das Schweigen eine Antwort; der Satz sagt das, statt "antwortet:
    // kein Zeichen" zu schreiben.
    const antwortDe = z.pattern.length === 0 ? `${gross(werDe)} gibt kein Zeichen.` : `${gross(werDe)} antwortet: ${klingt(z, 'de')}.`
    const antwortEn = z.pattern.length === 0 ? `${gross(werEn)} gives no signal.` : `${gross(werEn)} answers: ${klingt(z, 'en')}.`
    return {
      de: `${vorherDe} hat gesagt: „${vorher.bedeutung}“. ${antwortDe} Was heißt das?`,
      en: `${vorherEn} has said: "${vorher.bedeutung}". ${antwortEn} What does that mean?`,
    }
  }
  switch (z.abschnitt) {
    case 'A':
      return {
        de: `Allgemeines Zeichen, also nicht bei Begegnung oder Überholen: ${klingt(z, 'de')}. Was bedeutet es?`,
        en: `General signal, not when meeting or overtaking: ${klingt(z, 'en')}. What does it mean?`,
      }
    case 'B':
      return {
        de: `Zwei Fahrzeuge begegnen sich. ${gross(werDe)} gibt als Erster: ${klingt(z, 'de')}. Was verlangt er?`,
        en: `Two vessels meet. ${gross(werEn)} gives first: ${klingt(z, 'en')}. What is she asking for?`,
      }
    case 'C':
      return {
        de: `Beim Überholen gibt ${werDe} von sich aus: ${klingt(z, 'de')}. Was heißt das?`,
        en: `When overtaking, ${werEn} gives of her own accord: ${klingt(z, 'en')}. What does that mean?`,
      }
    case 'D':
      return {
        de: `Ein Fahrzeug will wenden und gibt: ${klingt(z, 'de')}. Was kündigt es an?`,
        en: `A vessel wants to turn round and gives: ${klingt(z, 'en')}. What is she announcing?`,
      }
    case 'E':
      return {
        de: `Ein Fahrzeug kommt aus einem Hafen oder einer Nebenwasserstraße und gibt: ${klingt(z, 'de')}. Was kündigt es an?`,
        en: `A vessel comes out of a port or a side waterway and gives: ${klingt(z, 'en')}. What is she announcing?`,
      }
    case 'G':
      return {
        de: `Bei unsichtigem Wetter hörst du: ${klingt(z, 'de')}. Wer gibt das?`,
        en: `In restricted visibility you hear: ${klingt(z, 'en')}. Who gives it?`,
      }
  }
}

/**
 * Die Frage zu einem Binnen-Schallzeichen.
 *
 * Die falschen Antworten sind bevorzugt Zeichen mit demselben Ton aus einer anderen Lage.
 * Bei den Seesignalen ist das ausgeschlossen, weil ein Ton dort nur eine Bedeutung hat
 * und zwei gleiche Töne zwei richtige Antworten wären. Auf dem Rhein ist die
 * Mehrdeutigkeit des Tons der Stoff: wer "ein kurzer Ton heißt Steuerbord" gelernt hat,
 * fällt bei der Begegnung darauf herein. Fair ist das, weil die Frage die Lage nennt.
 */
export function binnenSignalQuestion(z: BinnenSignalData, random: () => number): QuizQuestion {
  const muster = JSON.stringify(z.pattern)
  const distractors = pickDistractors(
    binnenSignale,
    z,
    (x) => x.bedeutung,
    (x) => JSON.stringify(x.pattern) === muster,
    random,
  )
  const options = shuffle(
    [z, ...distractors].map((x) => ({ id: x.id, label: { de: x.bedeutung, en: x.bedeutung } })),
    random,
  )
  return {
    kind: 'binnen-signal',
    subject: z.id,
    prompt: binnenSignalPrompt(z),
    options,
    correct: options.findIndex((o) => o.id === z.id),
    cardId: `binnenSignal:${z.id}`,
  }
}

/** Alle Karten, die es im Bestand gibt. Grundlage für Fortschritt und Wiederholung. */
export function allCardIds(): string[] {
  return [
    ...lightStates.map((s) => `state:${s.id}`),
    ...buoys.map((b) => `buoy:${b.id}`),
    ...signals.map((s) => `signal:${s.id}`),
  ]
}

/**
 * Die Karten des Binnenbestands, getrennt geführt.
 *
 * Sie stehen bewusst nicht in `allCardIds()`: der Lernstand und das Quiz zeigen den
 * Bestand, den der Nutzer gewählt hat, und ein Binnenbild zwischen Seezuständen wäre
 * genau die Vermischung, die dieser Block verhindern soll.
 */
export function binnenCardIds(): string[] {
  return [
    ...binnenStates.map((s) => `binnen:${s.id}`),
    ...binnenSignale.map((z) => `binnenSignal:${z.id}`),
  ]
}

/** Baut die Frage zu einer Karten-ID. */
export function questionFor(cardId: string, random: () => number): QuizQuestion {
  const [kind, id] = [cardId.slice(0, cardId.indexOf(':')), cardId.slice(cardId.indexOf(':') + 1)]
  if (kind === 'signal') {
    const signal = signals.find((s) => s.id === id)
    if (!signal) throw new Error(`Schallsignal ${id} gibt es nicht`)
    return signalQuestion(signal, random)
  }
  if (kind === 'buoy') {
    const buoy = buoys.find((b) => b.id === id)
    if (!buoy) throw new Error(`Seezeichen ${id} gibt es nicht`)
    return buoyQuestion(buoy, random)
  }
  if (kind === 'binnen') return binnenStateQuestion(binnenState(id), random)
  if (kind === 'binnenSignal') {
    const zeichen = binnenSignale.find((z) => z.id === id)
    if (!zeichen) throw new Error(`Binnen-Schallzeichen ${id} gibt es nicht`)
    return binnenSignalQuestion(zeichen, random)
  }
  const state = lightStates.find((s) => s.id === id)
  if (!state) throw new Error(`Lichterzustand ${id} gibt es nicht`)
  return lightStateQuestion(state, random)
}
